import fs from 'node:fs';
import path from 'node:path';
import { resolveCliGitAuth } from '../utils/cli-auth-storage.js';

interface GitCredentialOptions {
  endpoint?: string;
  startDir?: string;
  input?: string;
  fetchImpl?: typeof fetch;
  write?: (text: string) => void;
  /** Diagnostic sink — git surfaces a credential helper's stderr to the user,
   *  so failures must say exactly what to run instead of dying silently. */
  warn?: (text: string) => void;
}

const GIT_LOGIN_COMMAND = 'npx @standardagents/cli@latest git-login';

interface CredentialRequest {
  protocol?: string;
  host?: string;
  path?: string;
}

interface ArtifactGitResponse {
  artifact_git?: {
    token_secret?: string;
  };
}

type GitCredentialConfig =
  | { endpoint: string; auth: { kind: 'api-key'; apiKey: string } }
  | { endpoint: string; auth: { kind: 'session-cookie'; cookie: string } };

function parseKeyValueLines(input: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const line of input.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const index = line.indexOf('=');
    if (index <= 0) continue;
    result[line.slice(0, index)] = line.slice(index + 1);
  }
  return result;
}

function findDotDevVars(startDir = process.cwd()): string | null {
  let current = path.resolve(startDir);
  while (true) {
    const candidate = path.join(current, '.dev.vars');
    if (fs.existsSync(candidate)) return candidate;
    const parent = path.dirname(current);
    if (parent === current) return null;
    current = parent;
  }
}

function normalizeEndpoint(value: string): string {
  return value.replace(/\/+$/, '');
}

function normalizeGitHost(host: string): string {
  return host.trim().toLowerCase().replace(/:\d+$/, '');
}

function isStandardAgentsGitHost(host: string): boolean {
  const normalized = normalizeGitHost(host);
  return normalized === 'standardagents.ai' ||
    normalized.endsWith('.standardagents.ai') ||
    normalized === 'standardagentbuilder.com' ||
    normalized.endsWith('.standardagentbuilder.com') ||
    normalized === 'artifacts.cloudflare.net' ||
    normalized.endsWith('.artifacts.cloudflare.net') ||
    normalized === 'artifacts.cloudflare.com' ||
    normalized.endsWith('.artifacts.cloudflare.com') ||
    normalized === 'git.cloudflare.com';
}

function loadLocalConfig(options: GitCredentialOptions): GitCredentialConfig | null {
  const envPath = findDotDevVars(options.startDir);
  const fileVars = envPath ? parseKeyValueLines(fs.readFileSync(envPath, 'utf-8')) : {};
  const endpoint = normalizeEndpoint(
    options.endpoint ||
    process.env.STANDARD_AGENTS_API_URL ||
    process.env.PLATFORM_ENDPOINT ||
    fileVars.STANDARD_AGENTS_API_URL ||
    fileVars.PLATFORM_ENDPOINT ||
    ''
  );
  const apiKey = process.env.STANDARD_AGENTS_API_KEY || fileVars.STANDARD_AGENTS_API_KEY || '';
  if (endpoint && apiKey) {
    return { endpoint, auth: { kind: 'api-key', apiKey } };
  }

  const cookie = process.env.STANDARD_AGENTS_SESSION_COOKIE ||
    process.env.STANDARDAGENTS_BOOTSTRAP_SESSION_COOKIE ||
    fileVars.STANDARDAGENTS_BOOTSTRAP_SESSION_COOKIE ||
    '';
  if (endpoint && cookie) {
    return { endpoint, auth: { kind: 'session-cookie', cookie } };
  }

  const globalAuth = resolveCliGitAuth();
  if (!globalAuth) return null;
  const requestedEndpoint = endpoint || normalizeEndpoint(options.endpoint || '');
  if (requestedEndpoint && requestedEndpoint !== globalAuth.endpoint) {
    return null;
  }
  // Prefer the long-lived API key over the session cookie: sessions expire in
  // days and used to strand pushes with an opaque git auth error.
  if (globalAuth.api_key) {
    return {
      endpoint: globalAuth.endpoint,
      auth: { kind: 'api-key', apiKey: globalAuth.api_key },
    };
  }
  return {
    endpoint: globalAuth.endpoint,
    auth: { kind: 'session-cookie', cookie: globalAuth.session_cookie },
  };
}

function remoteFromCredentialRequest(request: CredentialRequest): string | null {
  if (!request.protocol || !request.host) return null;
  if (!isStandardAgentsGitHost(request.host)) return null;
  const repoPath = request.path ? `/${request.path.replace(/^\/+/, '')}` : '';
  return `${request.protocol}://${normalizeGitHost(request.host)}${repoPath}`;
}

async function readStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) {
    chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(String(chunk)));
  }
  return Buffer.concat(chunks).toString('utf-8');
}

export async function gitCredential(operation: string, options: GitCredentialOptions = {}): Promise<void> {
  if (operation !== 'get') return;

  const warn = options.warn ?? ((text: string) => {
    process.stderr.write(`standardagents: ${text}\n`);
  });

  const input = options.input ?? await readStdin();
  const remote = remoteFromCredentialRequest(parseKeyValueLines(input));
  // Foreign hosts stay silent — git falls through to the user's own helpers.
  if (!remote) return;

  const config = loadLocalConfig(options);
  if (!config) {
    warn(`No Standard Agents git credentials found. Run: ${GIT_LOGIN_COMMAND}`);
    return;
  }

  const fetchImpl = options.fetchImpl ?? fetch;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (config.auth.kind === 'api-key') {
    headers.Authorization = `Bearer ${config.auth.apiKey}`;
  } else {
    headers.Cookie = config.auth.cookie;
  }

  const response = await fetchImpl(`${config.endpoint}/projects/git-token`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ remote, scope: 'write' }),
  }).catch(() => null);
  if (!response?.ok) {
    if (!response) {
      warn(`Could not reach ${config.endpoint} to mint git credentials — check your connection and retry.`);
    } else if (response.status === 401 || response.status === 403) {
      warn(
        config.auth.kind === 'api-key'
          ? `Your Standard Agents API key was revoked or lost access. Run: ${GIT_LOGIN_COMMAND}`
          : `Your Standard Agents sign-in expired. Run: ${GIT_LOGIN_COMMAND}`,
      );
    } else {
      warn(`Minting git credentials failed (HTTP ${response.status}). Retry, or re-auth with: ${GIT_LOGIN_COMMAND}`);
    }
    return;
  }

  const body = await response.json().catch(() => null) as ArtifactGitResponse | null;
  const tokenSecret = body?.artifact_git?.token_secret;
  if (!tokenSecret) {
    warn(`The platform returned no git token for ${remote}. Re-auth with: ${GIT_LOGIN_COMMAND}`);
    return;
  }

  const write = options.write ?? ((text: string) => {
    process.stdout.write(text);
  });
  write(`username=x\npassword=${tokenSecret}\n\n`);
}
